import { useCallback } from 'react';
import axios from 'axios';
import { getDecryptLocalStorage } from '../Utils/localStorageHelper';
import { IAttachmentExtension } from '../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { t } from 'i18next';

// use this centralized component to download files from Google Drive
// refer to Rappit team for more information
interface FileCapabilities {
    canAcceptOwnership?: boolean;
    canAddChildren?: boolean;
    canAddMyDriveParent?: boolean;
    canChangeCopyRequiresWriterPermission?: boolean;
    canChangeSecurityUpdateEnabled?: boolean;
    canChangeViewersCanCopyContent?: boolean;
    canComment?: boolean;
    canCopy?: boolean;
    canDelete?: boolean;
    canDownload?: boolean;
    canEdit?: boolean;
    canListChildren?: boolean;
    canModifyContent?: boolean;
    canModifyContentRestriction?: boolean;
    canModifyEditorContentRestriction?: boolean;
    canModifyOwnerContentRestriction?: boolean;
    canModifyLabels?: boolean;
    canMoveChildrenWithinDrive?: boolean;
    canMoveItemIntoTeamDrive?: boolean;
    canMoveItemOutOfDrive?: boolean;
    canMoveItemWithinDrive?: boolean;
    canReadLabels?: boolean;
    canReadRevisions?: boolean;
    canRemoveChildren?: boolean;
    canRemoveContentRestriction?: boolean;
    canRemoveMyDriveParent?: boolean;
    canRename?: boolean;
    canShare?: boolean;
    canTrash?: boolean;
    canUntrash?: boolean;
}

const useGdriveDownload = (confirm: any, allowedMimeTypes: IAttachmentExtension[]) => {
    const mimeTypeToExtension: Record<string, string> = allowedMimeTypes.reduce(
        (acc, { mimeType, value }) => {
            acc[mimeType!] = value!;
            return acc;
        },
        {} as Record<string, string>
    );

    const getFileNameWithExtension = (fileName: string, mimeType: string): string => {
        const extension = mimeTypeToExtension![mimeType] || ''; // Get extension or fallback to empty string
        const baseName = fileName.replace(/\.[^/.]+$/, ''); // Remove any existing extension
        return `${baseName}${extension}`;
    };

    const checkFileCapabilties = useCallback(async (fileIds: string[]) => {
        try {
            await Promise.all(
                fileIds.map(async (fileId) => {
                    const response = await axios.get(`https://www.googleapis.com/drive/v3/files/${fileId}?fields=capabilities`, {
                        headers: {
                            Authorization: `Bearer ${getDecryptLocalStorage('pc_t', true)}`,
                        },
                        params: { supportsAllDrives: true }
                    });

                    const metadata = await response.data;
                    const capabilities = (metadata?.capabilities as FileCapabilities);

                    if (!capabilities.canDownload) {
                        throw new Error('readOnlyError');
                    }
                })
            );
        } catch (error: any) {
            if (error.message === 'readOnlyError') {
                confirm({
                    show: true,
                    title: t(`googlePicker.dialog.warningTitle`),
                    message: t('googlePicker.dialog.error.readOnly'),
                    confirmText: t('Ok'),
                });
                throw error;
            }
        }
    }, []);

    const getFilesByIds = useCallback(async (fileIds: string[], fileNames: string[]): Promise<FileList | null> => {
        const files: File[] = [];

        try {
            await checkFileCapabilties(fileIds);
        } catch (error) {
            console.error('Error in file capabilities check:', error);
            return null;
        }

        const filePromises = fileIds.map(async (fileId, index) => {
            const fileName = fileNames[index];
            let currentMimeType: string | undefined;
            
            try {
                // Check file type and decide the endpoint
                const response = await axios.get(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
                    headers: {
                        Authorization: `Bearer ${getDecryptLocalStorage('pc_t', true)}`,
                    },
                    params: { fields: 'mimeType', supportsAllDrives: true },
                });

                currentMimeType = response.data.mimeType;

                // If the file is a Google Workspace file, use the export endpoint
                let fileResponse;
                if (currentMimeType?.startsWith('application/vnd.google-apps')) {
                    // Map exportable formats (e.g., Google Slides -> PDF)
                    const exportMimeType = getExportMimeType(currentMimeType);
                    fileResponse = await axios.get(`https://www.googleapis.com/drive/v3/files/${fileId}/export`, {
                        headers: {
                            Authorization: `Bearer ${getDecryptLocalStorage('pc_t', true)}`,
                        },
                        responseType: 'blob',
                        params: { supportsAllDrives: true, mimeType: exportMimeType }
                    });
                } else {
                    // For binary files, use the regular download endpoint
                    fileResponse = await axios.get(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
                        headers: {
                            Authorization: `Bearer ${getDecryptLocalStorage('pc_t', true)}`,
                        },
                        responseType: 'blob',
                        params: { supportsAllDrives: true }
                    });
                }

                // Create a Blob from the response data
                const blob = new Blob([fileResponse.data], { type: fileResponse.headers['content-type'] });

                const fileNameWithExtension = getFileNameWithExtension(fileName, fileResponse.headers['content-type']);

                // Create a File object from the Blob
                const file = new File([blob], fileNameWithExtension, { type: fileResponse.headers['content-type'], lastModified: Date.now() });
                return file;
            } catch (error) {
                if (axios.isAxiosError(error) && error.response) {
                    try {
                        let errorResponse = await error.response.data.text();
                        const errorObj = JSON.parse(errorResponse);
                        const errorReason = errorObj?.error?.errors[0]?.reason;
                        const originalErrorMessage = errorObj?.error?.errors[0]?.message;
                        
                        if (errorReason && errorReason === 'exportSizeLimitExceeded' && currentMimeType) {
                            return await maxSizeErrorFallBack(fileId, fileName, currentMimeType);
                        }
                        else if (errorReason && originalErrorMessage) {
                            // if no specific error message is found, show a generic error message along with the original error message
                            var errorMessage = t(`googlePicker.dialog.error.${errorReason}`) !== `googlePicker.dialog.error.${errorReason}`
                                ? t(`googlePicker.dialog.error.${errorReason}`) :
                                `${t(`googlePicker.dialog.error.fileConversionWithExplanation`) + originalErrorMessage}`;
                            confirm({
                                show: true,
                                title: t(`googlePicker.dialog.warningTitle`),
                                message: errorMessage,
                                confirmText: t('Ok'),
                            });
                        }
                        else {
                            confirm({
                                show: true,
                                title: t(`googlePicker.dialog.warningTitle`),
                                message: t('googlePicker.dialog.error.fileConversion'),
                                confirmText: t('Ok'),
                            });
                        }
                    } catch (parseError) {
                        console.error('Error parsing error response:', parseError);
                        confirm({
                            show: true,
                            title: t(`googlePicker.dialog.warningTitle`),
                            message: t('googlePicker.dialog.error.fileConversion'),
                            confirmText: t('Ok'),
                        });
                    }
                }
                else {
                    console.error('Error retrieving files:', error);
                    confirm({
                        show: true,
                        title: t(`googlePicker.dialog.warningTitle`),
                        message: t('googlePicker.dialog.error.fileConversion'),
                        confirmText: t('Ok'),
                    });
                }
                return null;
            }
        });

        try {
            const downloadedFiles = await Promise.all(filePromises);
            
            // Filter out any null results and add the files to our array
            for (const file of downloadedFiles) {
                if (file !== null) {
                    files.push(file);
                }
            }

            // Create a FileList from the array of Files
            return createFileList(files);
        } catch (error) {
            console.error('Error processing downloaded files:', error);
            return null;
        }
    }, []);

    // Helper function to determine export MIME type
    const getExportMimeType = (mimeType: string): string => {
        const exportMimeTypes: Record<string, string> = {
            'application/vnd.google-apps.document': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // Google Docs to DOCX
            'application/vnd.google-apps.spreadsheet': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // Google Sheets to XLSX
            'application/vnd.google-apps.presentation': 'application/vnd.openxmlformats-officedocument.presentationml.presentation', // Google Slides to PPTX
        };
        return exportMimeTypes[mimeType] || 'application/pdf'; // Default to PDF if no match
    };

    const getFallBackUrl = (fileId: string, fileType: string, extension: string): string => {
        let url;
        switch(fileType) {
            case 'document': url = `https://docs.google.com/${fileType!}/d/${fileId}/export?format=${extension!}`; break;
            case 'spreadsheet': url = `https://docs.google.com/${fileType!}s/d/${fileId}/export?format=${extension!}`; break;
            case 'presentation': url = `https://docs.google.com/${fileType!}/d/${fileId}/export/${extension!}`; break;
            default: url = '';
        }
        return url!;
    }

    const maxSizeErrorFallBack = async (fileId: string, fileName: string, mimeType: string): Promise<File | null> => {
        const exportMimeType = getExportMimeType(mimeType);
        const fileType = mimeType.split(".").pop();
        const extension = mimeTypeToExtension![exportMimeType].split(".").pop();
        const fallbackUrl = getFallBackUrl(fileId, fileType!, extension!);

        if (!fallbackUrl) {
            return null;
        }

        try {
            const fallbackResponse = await axios.get(fallbackUrl, {
                headers: {
                    Authorization: `Bearer ${getDecryptLocalStorage('pc_t', true)}`,
                },
                responseType: 'blob',
                params: { supportsAllDrives: true }
            });
            
            const blob = new Blob([fallbackResponse.data], { type: fallbackResponse.headers['content-type'] });
            const fileNameWithExtension = getFileNameWithExtension(fileName, fallbackResponse.headers['content-type']);
            const file = new File([blob], fileNameWithExtension, { type: fallbackResponse.headers['content-type'], lastModified: Date.now() });
            return file;
        } catch (error) {
            console.error('Error in fallback download:', error);
            return null;
        }
    }

    // Helper function to create a FileList
    const createFileList = (files: File[]): FileList => {
        const dataTransfer = new DataTransfer();
        files.forEach(file => dataTransfer.items.add(file));
        return dataTransfer.files; // Return the FileList object
    };

    return { getFilesByIds };
};

export default useGdriveDownload;